<template>
  <Modal
    :title="modalTitle"
    :is-large="!readOnly"
    :model-value="modelValue"
    v-on:update:model-value="(value: boolean) => $emit('update:modelValue', value)"
  >
    <div class="columns contains-menu">
      <div
        v-if="!readOnly"
        class="column is-narrow has-overflow-auto"
      >
        <div class="tabs is-centered mb-2">
          <ul v-if="!readOnly">
            <li
              :class="{ 'is-active': !archivedFilter }"
              v-on:click="archivedFilter = false"
            >
              <a>Enabled</a>
            </li>
            <li
              :class="{ 'is-active': archivedFilter }"
              v-on:click="archivedFilter = true"
            >
              <a>Archived</a>
            </li>
          </ul>
        </div>
        <aside class="menu">
          <ul class="menu-list">
            <li>
              <a
                class="no-config"
                :class="{ 'is-active': !selectedConfigurationId && !configCreate }"
                v-on:click="setConfiguration(null)"
              >
                No configuration
              </a>
            </li>
            <li>
              <a
                v-on:click="createNewConfiguration"
                :class="{ 'is-active': configCreate }"
              >
                <i class="icon-add-square"></i>
                New configuration
              </a>
            </li>
            <li v-if="loading">
              <a><div class="loader is-size-4 mx-auto"></div></a>
            </li>
            <template v-else-if="!configurationsError && workerId">
              <li
                v-for="configuration in workerConfigurationStore.configurations[workerId]"
                :key="configuration.id"
              >
                <a
                  :class="{
                    'is-active': selectedConfigurationId === configuration.id && !configCreate,
                  }"
                  v-on:click="setConfiguration(configuration.id)"
                  :title="configuration.name"
                >
                  {{ truncateShort(configuration.name) }}
                </a>
              </li>
            </template>
          </ul>
        </aside>
      </div>
      <div class="column">
        <template v-if="selectedConfigurationId && selectedConfiguration && !configCreate">
          <div class="level">
            <span class="level-left transcription-box">
              <EditableName
                :instance="selectedConfiguration"
                :save-function="renameConfiguration"
                class="subtitle"
                :enabled="!readOnly && !selectedConfiguration.archived"
              />
            </span>
            <div class="buttons level-right">
              <button
                type="button"
                class="button is-light mb-0"
                :class="{
                  'is-loading': loading,
                  'is-info': archiveButtonState,
                  'is-danger': !archiveButtonState,
                }"
                :disabled="!canToggleArchive || undefined"
                :title="archiveButtonTitle"
                v-on:click="toggleArchive"
              >
                {{ archiveButtonState ? 'Enable' : 'Archive' }}
              </button>
              <button
                :disabled="readOnly"
                class="button is-primary is-light mb-0"
                title="Clone this configuration"
                v-on:click="cloneConfiguration"
              >
                Clone
              </button>
            </div>
          </div>
          <!-- Do not put any spaces or line breaks between the <pre> and its contents, or the JSON indentation will be messed up -->
          <pre>{{ prettify(selectedConfiguration.configuration) }}</pre>
        </template>
        <div v-else-if="configCreate">
          <CreateForm
            v-on:set-configuration="setConfiguration"
            v-on:toggle-archived="archivedFilter = $event"
            v-on:config-created="((configCreate = false), (archivedFilter = false))"
            :worker-id="workerId"
            :worker-version-id="workerVersionId"
            :new-configuration="newConfiguration"
          />
        </div>
        <div
          v-else-if="configurationsError"
          class="notification is-danger"
        >
          {{ configurationsError }}
        </div>
        <div
          v-else
          class="notification is-info"
        >
          Select a <strong>worker configuration</strong> on the left panel to see its details.
        </div>
      </div>
    </div>
    <template v-slot:footer>
      <button
        type="button"
        class="button is-primary"
        :class="{ 'is-loading': loading }"
        :disabled="!canSave || undefined"
        :title="saveButtonTitle"
        v-on:click="saveConfiguration"
      >
        Select
      </button>
    </template>
  </Modal>
</template>

<script lang="ts">
import { mapActions, mapStores } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import type { WorkerConfigurationCreatePayload } from '@/api'
import EditableName from '@/components/EditableName.vue'
import Modal from '@/components/Modal.vue'
import { errorParser } from '@/helpers'
import { truncateMixin } from '@/mixins'
import { useNotificationStore, useProcessStore, useWorkerConfigurationStore } from '@/stores'
import type { UUID } from '@/types'
import type { WorkerRun } from '@/types/process'
import type { WorkerVersion } from '@/types/worker'
import type { WorkerConfiguration } from '@/types/workerConfiguration'

import CreateForm from './Create.vue'

export default defineComponent({
  components: {
    Modal,
    CreateForm,
    EditableName,
  },
  mixins: [truncateMixin],
  emits: {
    'selected-configuration': (value: WorkerConfiguration | undefined) =>
      value === undefined || (typeof value === 'object' && value.id),
    'update:modelValue': (value: boolean) => typeof value === 'boolean',
  },
  props: {
    /*
     * This component supports two exclusives modes:
     * * Emitting a select-configuration event when the workerVersion property is set
     * * Managing worker runs property when workerRun property are set
     */
    modelValue: {
      type: Boolean,
      default: false,
    },
    workerVersion: {
      type: Object as PropType<WorkerVersion | null>,
      default: null,
    },
    workerRun: {
      type: Object as PropType<WorkerRun | null>,
      default: null,
    },
    configurationId: {
      type: String as PropType<UUID>,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    archivedFilter: false,
    /**
     * Clicking the 'create configuration' button should always set configCreate to true
     * to show the creation form, and clicking on 'no configuration' or on some other
     * configuration in the list should close the form by setting configCreate to false.
     */
    configCreate: false,
    loading: false,
    configurationsError: null as string | null,
    // selectedConfigurationId stores the current selected configuration before it is confirmed by the user (Save)
    selectedConfigurationId: null as UUID | null,
    newConfiguration: {
      name: '',
      configuration: {},
    } as WorkerConfigurationCreatePayload,
  }),
  mounted() {
    // Ensure that properties have been passed correctly
    if ((this.workerRun !== null) !== (this.workerVersion === null)) {
      throw new Error('Exactly one of workerRun or workerVersion property must be set')
    }

    this.selectedConfigurationId = this.configurationId || null
  },
  computed: {
    ...mapStores(useWorkerConfigurationStore),
    modalTitle() {
      if (this.readOnly)
        return `Details for configuration ${this.truncateShort(this.selectedConfiguration?.name)}`
      if (this.workerVersion !== null) {
        return `Configure worker ${this.workerVersion.worker.name}`
      } else {
        return `Configure ${this.workerRun?.worker_version?.worker?.name}`
      }
    },
    processId() {
      return this.workerRun?.process?.id
    },
    workerId() {
      if (this.workerVersion !== null) {
        return this.workerVersion.worker.id
      } else if (this.workerRun !== null) {
        return this.workerRun.worker_version.worker.id
      }
      throw new Error('There is no workerRun or workerVersion')
    },
    workerVersionId() {
      if (this.workerVersion !== null) {
        return this.workerVersion.id
      } else if (this.workerRun !== null) {
        return this.workerRun.worker_version.id
      }
      throw new Error('There is no workerRun or workerVersion')
    },
    selectedConfiguration() {
      if (!this.selectedConfigurationId || !this.workerId) return
      return this.workerConfigurationStore.configurations[this.workerId]?.[
        this.selectedConfigurationId
      ]
    },
    canSave() {
      return (
        !this.loading &&
        // If a configuration ID is set, we must have this configuration in the store
        !this.selectedConfigurationId === !this.selectedConfiguration &&
        // Do not allow saving the currently selected worker configuration if said configuration is archived
        !this.selectedConfiguration?.archived &&
        // Do not allow saving while the configuration creation form is active
        !this.configCreate &&
        // Disable saving in read only mode
        !this.readOnly
      )
    },
    canToggleArchive() {
      // This cannot check that the user has enough rights on the worker, because the API does not provide that anywhere.
      return !this.loading && !this.readOnly && !this.configCreate && this.selectedConfiguration
    },
    /**
     * Determines whether the archive button should be an Enable button (true) or an Archive button (false).
     * When a configuration is selected, its `archived` property is used; otherwise, we will display the
     * state that matches the current `archivedFilter`, but disabled, just for consistency.
     * @type {boolean}
     */
    archiveButtonState() {
      return this.selectedConfiguration?.archived ?? this.archivedFilter
    },
    archiveButtonTitle() {
      if (this.canToggleArchive) {
        if (this.archiveButtonState) return 'Enable this configuration'
        else return 'Archive this configuration'
      } else {
        if (this.archiveButtonState)
          return 'You must select an existing archived configuration in order to enable it.'
        else return 'You must select an existing enabled configuration in order to archive it.'
      }
    },
    saveButtonTitle() {
      if (this.canSave && this.selectedConfigurationId) return 'Select this configuration.'
      if (this.configCreate)
        return 'Create the new configuration first, or select an existing configuration to use.'
      if (this.loading) return 'Loading…'
      if (this.selectedConfiguration?.archived)
        return 'An archived configuration cannot be selected in a process.'
      return ''
    },
  },
  methods: {
    ...mapActions(useNotificationStore, ['notify']),
    ...mapActions(useProcessStore, ['updateWorkerRun']),

    createNewConfiguration() {
      // When clicking on "New configuration" the pre-filled value is reset
      this.newConfiguration = {
        name: '',
        configuration: {},
      }
      this.configCreate = true
    },

    async retrieveConfigurations() {
      if (!this.workerId || this.readOnly) return
      this.configurationsError = null
      this.loading = true
      try {
        await this.workerConfigurationStore.list(this.workerId, { archived: this.archivedFilter })
      } catch (err) {
        this.configurationsError = errorParser(err)
      } finally {
        this.loading = false
      }
    },

    async setConfiguration(configId: UUID | null) {
      if (this.readOnly || !this.workerId) return
      if (this.selectedConfigurationId !== configId) {
        if (
          configId !== null &&
          !this.workerConfigurationStore.configurations[this.workerId][configId]
        ) {
          this.loading = true
          try {
            await this.workerConfigurationStore.get(this.workerId, configId)
            this.archivedFilter =
              this.workerConfigurationStore.configurations[this.workerId][configId].archived
          } catch (err) {
            this.notify({ type: 'error', text: errorParser(err) })
            return
          } finally {
            this.loading = false
          }
        }
        this.selectedConfigurationId = configId
      }
      if (this.configCreate) this.configCreate = false
    },

    async saveConfiguration() {
      /*
       * Saves the selected configuration to the process in worker run mode
       * Emits a selected-configuration in simple worker mode
       */
      if (!this.canSave || !this.workerId || !this.processId || !this.workerRun) return

      // In worker mode, simply emit the selected configuration and close the modal
      if (this.workerVersion !== null) {
        this.$emit('selected-configuration', this.selectedConfiguration)
        this.$emit('update:modelValue', false)
        return
      }

      // Do nothing if the configuration has been left unchanged
      if (this.selectedConfigurationId === this.configurationId) {
        if (this.configCreate) this.configCreate = false
        this.$emit('update:modelValue', false)
        return
      }

      this.loading = true
      const payload = { configuration_id: this.selectedConfigurationId }
      /**
       * Get the name of the configuration for the success notification now, in order to avoid a
       * potential race condition as updateWorkerRun causes workerId to be re-computed when a
       * configuration is first saved for a newly added worker run, which triggers
       * retrieveConfigurations again through a watcher.
       * When removing a configuration, selectedConfigurationId is null.
       */
      let configurationName = ''
      if (this.selectedConfigurationId)
        configurationName =
          this.workerConfigurationStore.configurations[this.workerId][this.selectedConfigurationId]
            .name
      try {
        await this.updateWorkerRun(this.processId, this.workerRun.id, payload)
        if (this.selectedConfigurationId) {
          this.notify({
            type: 'success',
            text: `Configuration ${configurationName} added to worker run.`,
          })
        } else {
          this.notify({ type: 'success', text: 'Configuration removed from worker run.' })
        }
        this.$emit('update:modelValue', false)
        if (this.configCreate) this.configCreate = false
      } catch (err) {
        this.notify({ type: 'error', text: errorParser(err) })
      } finally {
        this.loading = false
      }
    },

    async toggleArchive() {
      if (!this.canToggleArchive || !this.selectedConfiguration || !this.workerId) return
      try {
        this.loading = true
        const newArchivedStatus = !this.selectedConfiguration?.archived
        await this.workerConfigurationStore.update(this.workerId, this.selectedConfiguration.id, {
          archived: newArchivedStatus,
        })
        // Unselect the configuration if it has been archived
        if (newArchivedStatus) this.selectedConfigurationId = null
        // If the configuration has been enabled, switch off archivedFilter and keep it selected
        else this.archivedFilter = false
      } finally {
        this.loading = false
      }
    },

    prettify: function (value: unknown) {
      return JSON.stringify(value, null, 2)
    },

    cloneConfiguration() {
      if (this.readOnly || !this.selectedConfiguration) return
      this.newConfiguration = {
        configuration: this.selectedConfiguration.configuration,
        name: 'Copy of ' + this.selectedConfiguration.name,
      }
      this.configCreate = true
    },

    /**
     * Used by the EditableName component
     */
    async renameConfiguration(name: string) {
      if (!this.workerId || !this.selectedConfigurationId) return
      await this.workerConfigurationStore.update(this.workerId, this.selectedConfigurationId, {
        name,
      })
      this.retrieveConfigurations()
    },
  },
  watch: {
    workerId: {
      immediate: true,
      handler() {
        this.retrieveConfigurations()
      },
    },
    configurationId: {
      immediate: true,
      handler(newValue: UUID | null) {
        this.selectedConfigurationId = newValue
        if (
          newValue !== null &&
          !this.workerConfigurationStore.configurations[this.workerId]?.[newValue]
        )
          this.workerConfigurationStore.get(this.workerId, newValue)
      },
    },
    archivedFilter(newValue, oldValue) {
      if (oldValue === newValue) return
      this.retrieveConfigurations()
    },
  },
})
</script>

<style scoped>
.has-overflow-auto {
  height: inherit;
  overflow: auto;
}
.contains-menu {
  height: 100%;
}
.no-config {
  font-style: italic;
}
.menu-list li:not(:last-child) .no-config {
  border-bottom: 2px solid lightgrey;
}
.tabs a {
  padding-top: 0;
}
pre {
  /* Break long strings into multiple lines in the configuration JSON display */
  white-space: pre-wrap;
}
</style>
